<template>
  <div class="merchant-list-container">
    <h1 class="page-title">商家列表</h1>
    <el-card class="content-card">
      <div class="card-header">
        <h2>商家列表</h2>
        <el-button type="primary" @click="handleAddMerchant">
          <el-icon><plus /></el-icon> 添加商家
        </el-button>
      </div>
      <div class="card-content">
        <el-table :data="merchants" border stripe style="width: 100%">
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column label="商家标志" width="120">
            <template #default="scope">
              <el-image
                v-if="scope.row.logo"
                :src="scope.row.logo"
                :fit="'cover'"
                style="width: 100px; height: 60px;"
              />
              <span v-else>无标志</span>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="商家名称" min-width="200" />
          <el-table-column prop="contact_name" label="联系人" width="120" />
          <el-table-column prop="contact_phone" label="联系电话" width="150" />
          <el-table-column prop="email" label="邮箱" min-width="200" />
          <el-table-column prop="status" label="状态" width="100">
            <template #default="scope">
              <el-tag :type="scope.row.status === true ? 'success' : 'danger'">
                {{ scope.row.status === true ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="sort_order" label="排序" width="100" />
          <el-table-column prop="created_at" label="创建时间" width="180" />
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button type="primary" size="small" @click="handleEditMerchant(scope.row)">
                <el-icon><edit /></el-icon> 编辑
              </el-button>
              <el-button type="danger" size="small" @click="handleDeleteMerchant(scope.row)">
                <el-icon><delete /></el-icon> 删除
              </el-button>
              <el-switch
                v-model="scope.row.status"
                @change="handleStatusChange(scope.row)"
                style="margin-left: 10px;"
              />
            </template>
          </el-table-column>
        </el-table>
        
        <div class="pagination-container">
          <el-pagination
            :current-page="pagination.currentPage"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>
    
    <!-- 添加/编辑商家对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEditMode ? '编辑商家' : '添加商家'"
      width="700px"
      @close="resetForm"
    >
      <el-form :model="merchantForm" :rules="merchantRules" ref="merchantFormRef" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="商家名称" prop="name">
              <el-input v-model="merchantForm.name" placeholder="请输入商家名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="联系人" prop="contact_name">
              <el-input v-model="merchantForm.contact_name" placeholder="请输入联系人" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="联系电话" prop="contact_phone">
              <el-input v-model="merchantForm.contact_phone" placeholder="请输入联系电话" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="邮箱" prop="email">
              <el-input v-model="merchantForm.email" type="email" placeholder="请输入邮箱" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="商家标志" prop="logo">
              <el-input v-model="merchantForm.logo" placeholder="请输入商家标志URL" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="排序" prop="sort_order">
              <el-input-number v-model="merchantForm.sort_order" :min="0" :max="999" placeholder="请输入排序号" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="地址" prop="address">
              <el-input v-model="merchantForm.address" placeholder="请输入地址" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="商家描述" prop="description">
              <el-input v-model="merchantForm.description" type="textarea" placeholder="请输入商家描述" rows="4" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="状态" prop="status">
              <el-switch v-model="merchantForm.status" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmitForm">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Edit, Delete } from '@element-plus/icons-vue';
import request from '../../api/axios';

export default {
  name: 'MerchantList',
  components: {
    Plus,
    Edit,
    Delete
  },
  setup() {
    const merchants = ref([]);
    const dialogVisible = ref(false);
    const isEditMode = ref(false);
    const merchantFormRef = ref(null);
    
    const pagination = ref({
      currentPage: 1,
      pageSize: 10,
      total: 0
    });
    
    const merchantForm = ref({
      name: '',
      contact_name: '',
      contact_phone: '',
      email: '',
      address: '',
      logo: '',
      description: '',
      status: true,
      sort_order: 0
    });
    
    const merchantRules = {
      name: [
        { required: true, message: '请输入商家名称', trigger: 'blur' }
      ],
      contact_name: [
        { required: true, message: '请输入联系人', trigger: 'blur' }
      ],
      contact_phone: [
        { required: true, message: '请输入联系电话', trigger: 'blur' }
      ]
    };
    
    // 获取商家列表
    const fetchMerchants = async () => {
      try {
        const response = await request({
          url: '/manage/merchants',
          method: 'get',
          params: {
            page: pagination.value.currentPage,
            page_size: pagination.value.pageSize
          }
        });
        merchants.value = response.data.data;
        pagination.value.total = response.data.total;
      } catch (error) {
        ElMessage.error('获取商家列表失败');
        console.error(error);
      }
    };
    
    // 页面加载时获取数据
    onMounted(() => {
      fetchMerchants();
    });
    
    // 打开添加商家对话框
    const handleAddMerchant = () => {
      isEditMode.value = false;
      resetForm();
      dialogVisible.value = true;
    };
    
    // 打开编辑商家对话框
    const handleEditMerchant = (merchant) => {
      isEditMode.value = true;
      merchantForm.value = { ...merchant };
      dialogVisible.value = true;
    };
    
    // 关闭对话框时重置表单
    const resetForm = () => {
      merchantForm.value = {
        name: '',
        contact_name: '',
        contact_phone: '',
        email: '',
        address: '',
        logo: '',
        description: '',
        status: true,
        sort_order: 0
      };
      if (merchantFormRef.value) {
        merchantFormRef.value.resetFields();
      }
    };
    
    // 提交表单
    const handleSubmitForm = async () => {
      if (!merchantFormRef.value) return;
      
      await merchantFormRef.value.validate(async (valid) => {
        if (valid) {
          try {
            if (isEditMode.value) {
              // 更新商家
              await request({
                url: `/manage/merchants/${merchantForm.value.id}`,
                method: 'put',
                data: merchantForm.value
              });
              ElMessage.success('更新成功');
            } else {
              // 创建商家
              await request({
                url: '/manage/merchants',
                method: 'post',
                data: merchantForm.value
              });
              ElMessage.success('创建成功');
            }
            dialogVisible.value = false;
            fetchMerchants();
          } catch (error) {
            ElMessage.error(isEditMode.value ? '更新失败' : '创建失败');
            console.error(error);
          }
        } else {
          return false;
        }
      });
    };
    
    // 删除商家
    const handleDeleteMerchant = (merchant) => {
      ElMessageBox.confirm('确定要删除该商家吗？', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await request({
            url: `/manage/merchants/${merchant.id}`,
            method: 'delete'
          });
          ElMessage.success('删除成功');
          fetchMerchants();
        } catch (error) {
          ElMessage.error('删除失败');
          console.error(error);
        }
      }).catch(() => {
        // 取消删除操作
      });
    };
    
    // 切换商家状态
    const handleStatusChange = async (merchant) => {
      try {
        await request({
          url: `/manage/merchants/${merchant.id}`,
          method: 'put',
          data: {
            status: merchant.status
          }
        });
        ElMessage.success('状态更新成功');
      } catch (error) {
        ElMessage.error('状态更新失败');
        // 恢复原状态
        merchant.status = !merchant.status;
        console.error(error);
      }
    };
    
    // 处理分页大小变化
    const handleSizeChange = (size) => {
      pagination.value.pageSize = size;
      fetchMerchants();
    };
    
    // 处理当前页变化
    const handleCurrentChange = (current) => {
      pagination.value.currentPage = current;
      fetchMerchants();
    };
    
    return {
      merchants,
      dialogVisible,
      isEditMode,
      merchantFormRef,
      pagination,
      merchantForm,
      merchantRules,
      handleAddMerchant,
      handleEditMerchant,
      resetForm,
      handleSubmitForm,
      handleDeleteMerchant,
      handleStatusChange,
      handleSizeChange,
      handleCurrentChange
    };
  }
};
</script>

<style scoped>
.merchant-list-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.card-content {
  padding: 0;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>